import { Button } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { SERVICE_MODE, URL_PATH } from '../../../../constants';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import { setIsAddNewServiceOperation, setIsEditService, setIsEditServiceOperation } from '../../../../store/reducer/services/ServiceOperationMonitorReducer';
import { getModeFromUrl, getVersionFromUrl } from '../../../../utilities/services/ServiceListUtils';
import classes from './ServiceOperationWrapper.module.scss';
import OperationDetailsWrapper from './operation-details/OperationDetailsWrapper';
import ServiceOperationList from './operation-list/ServiceOperationList';

interface IServiceOperationsWrapperProps {
  onServiceOperationEntered: (serviceOperationName: string) => void;
  viewMode: boolean;
  changeView: () => void;
  isEditService?: boolean;
}

export const ServiceOperationWrapper = ({ onServiceOperationEntered, viewMode, changeView, isEditService = false }: IServiceOperationsWrapperProps) => {
  const [isAddClicked, setIsAddClicked] = useState(false);
  const [isSaveClicked, setIsSaveClicked] = useState(false);
  const [isEditButtonClicked, setIsEditButtonClicked] = useState(false);
  const [editViewIndex, setEditViewIndex] = useState<number>(0);
  const [isViewButtonClicked, setIsViewButtonClicked] = useState(false);
  const { t: translate } = useTranslation();
  const history = useNavigate();
  const location = useLocation();
  const { serviceName } = useSelector((state) => state.serviceDetailsReducer);
  const dispatch = useDispatch<AppDispatch>();
  const serviceVersion = getVersionFromUrl();

  /**
   * Add Service Operation button click
   */
  const onAddHandler = () => {
    setIsAddClicked(true);
    setIsEditButtonClicked(false);
    if (isEditService) {
      dispatch(setIsAddNewServiceOperation(true));
    }
  };

  /**
   * Save Service Operation button click
   */
  const onSaveActionHandler = () => {
    setIsSaveClicked(true);
    if (isEditService) {
      dispatch(setIsAddNewServiceOperation(false));
    }
  };
  const onServiceOpsAdded = () => {
    setIsAddClicked(false);
    setIsSaveClicked(false);
    setIsEditButtonClicked(false);
    dispatch(
      setIsEditServiceOperation({
        isEditServiceOperation: false,
        index: undefined
      })
    );
  };

  const onBackBtnClickHandler = () => {
    const queryParam = location.state?.path;
    const cloneServiceName = location.state?.cloneServiceName ?? '';
    const registryVersion = location.state?.version ?? '';
    if (viewMode) {
      history(
        `${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_EXCEPTION
        }/${serviceName}/${serviceVersion}/${getModeFromUrl()}`,
        { state: { ...location.state, path: queryParam } }
      );
    } else {
      history(`${URL_PATH.SERVICE_HOME}${URL_PATH.CREATE_EXCEPTION}`, { state: { path: queryParam, cloneServiceName, version: registryVersion } });
    }
  };

  const onViewBackBtnClickHandler = () => {
    history(
      `${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_EXCEPTION
      }/${serviceName}/${serviceVersion}/${getModeFromUrl()}`,
      { state: location.state }
    );
  };

  const onFinishBtnClickHandler = () => {
    const queryParam = location.state?.path;
    dispatch(setIsEditService(false));
    history(
      `${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_SERVICE}/${serviceName}/${serviceVersion}/${SERVICE_MODE.EDIT}`,
      { state: { path: queryParam } }
    );
    changeView();
  };

  const onEditButtonClick = (index: number) => {
    setIsEditButtonClicked(true);
    setEditViewIndex(index);
    dispatch(
      setIsEditServiceOperation({ isEditServiceOperation: true, index })
    );
  };

  const onViewButtonClick = (index: number) => {
    setEditViewIndex(index);
    setIsViewButtonClicked(true);
  };

  const onCancelClick = () => {
    setIsEditButtonClicked(false);
    setIsAddClicked(false);
    setIsViewButtonClicked(false);
    dispatch(
      setIsEditServiceOperation({
        isEditServiceOperation: false,
        index: undefined
      })
    );
  };

  const { isEditService: editServiceClicked } = useSelector(
    (state) => state.serviceOperationMonitorReducer
  );

  useEffect(() => {
    if (editServiceClicked) {
      setIsViewButtonClicked(false);
    }
  }, [editServiceClicked]);

  const getServiceOperationWrapperHeader = () => {
    let header;
    if (isEditService) {
      header = `${translate('service_operation_wrapper.lbl_edit')} ${translate(
        'service_operation_wrapper.service_operation_wrapper_title'
      )}`;
    } else if (viewMode) {
      header = `${translate('service_operation_wrapper.lbl_view')} ${translate(
        'service_operation_wrapper.service_operation_wrapper_title'
      )}`;
    } else {
      header = `${translate('service_operation_wrapper.lbl_Add')} ${translate(
        'service_operation_wrapper.service_operation_wrapper_title'
      )}`;
    }
    return header;
  };

  return (
    <div
      className={`form-wrapper-container ${classes['service-operation-wrapper']}`}
    >
      <Row className={classes['service-operation-wrapper-header']}>
        <div className={classes['service-operation-wrapper-title']}>
          {getServiceOperationWrapperHeader()}
        </div>
        <div className={classes['service-operation-wrapper-sub-title']}>
          <div className={classes['service-operation-wrapper-sub-title-1']}>
            {translate(
              'service_operation_wrapper.service_operation_wrapper_sub_title_1'
            )}
          </div>
          <div className={classes['service-operation-wrapper-sub-title-2']}>
            {translate(
              'service_operation_wrapper.service_operation_wrapper_sub_title_2'
            )}
          </div>
        </div>
      </Row>
      <Row className={classes['service-operation-wrapper-body']}>
        {!isAddClicked && !isEditButtonClicked && !isViewButtonClicked && (
          <ServiceOperationList
            onAddHandler={onAddHandler}
            onEditHandler={onEditButtonClick}
            isServiceOpsEditButtonClicked={isEditButtonClicked}
            onViewHandler={onViewButtonClick}
            isViewMode={viewMode}
          />
        )}
        {(isAddClicked || isEditButtonClicked || isViewButtonClicked) && (
          <OperationDetailsWrapper
            isSaveClicked={isSaveClicked}
            onSaveActionHandler={onSaveActionHandler}
            onServiceOpsAdded={onServiceOpsAdded}
            onServiceOperationEnteredOnUi={onServiceOperationEntered}
            isEditButtonClick={isEditButtonClicked}
            editViewIndex={editViewIndex}
            onCancelClicked={onCancelClick}
            isAddNewServiceOperationClick={isAddClicked}
            isViewButtonClick={isViewButtonClicked}
            viewMode={viewMode}
            serviceEdit={isEditService || false}
          />
        )}
      </Row>
      <Row className={classes['service-operation-wrapper-btns']}>
        {!isAddClicked && !isViewButtonClicked && !isEditService && (
          <>
            <Col sm={8} />
            <Col sm={4} className={classes['btn-div']}>
              <Button
                variant="secondary"
                onClick={onBackBtnClickHandler}
                data-testid="back-btn"
              >
                {translate('service_operation_wrapper.btn_back')}
              </Button>
            </Col>
          </>
        )}

        {isEditService && (
          <Col sm={12}>
            <div className={`${classes['btn-container']} ci-button-group`}>
              <Button
                variant="secondary"
                className={classes['btn-back']}
                onClick={onViewBackBtnClickHandler}
                data-testid="back-btn-edit"
              >
                {translate('service_operation_wrapper.btn_back')}
              </Button>
              <Button
                variant="primary"
                className={classes['btn-finish']}
                onClick={onFinishBtnClickHandler}
                data-testid="btn-finish"
              >
                {translate('service_operation_wrapper.btn_finish')}
              </Button>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};
